import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Radar, X, ExternalLink, TrendingUp, TrendingDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { fetchJSON } from '../lib/api-client';
import { FillExpand } from './FillExpand';

interface CatalystEventSource {
  title: string;
  link: string;
  source?: string;
}

interface CatalystEvent {
  id: number;
  title: string;
  summary: string;
  category: 'supplier_retreat' | 'policy' | 'demand_shift' | 'capex' | 'supply_chain' | 'other';
  geography: 'TW' | 'US' | 'global';
  sources: CatalystEventSource[];
}

interface CatalystPick {
  symbol: string;
  name?: string;
  thesis: string;
  reasoning: string;
  confidence: number;
  setupType: 'second_order_beneficiary' | 'policy_play' | 'supply_chain' | 'direct';
  horizon: 'short' | 'mid' | 'long';
  relatedEventIds: number[];
  watchSignals: string[];
  risks: string[];
}

interface CatalystResp {
  market: string;
  events: CatalystEvent[];
  beneficiaries: CatalystPick[];
  losers: CatalystPick[];
  marketComment: string;
  newsCount: number;
  computedAt: string;
  modelUsed: string;
  /** 來源新聞發布時間範圍（取 allHits pubDate 的最早 / 最晚）。舊快取可能沒有。 */
  newsTimeRange?: { from: string; to: string };
  fromCache?: boolean;
}

/** 池外重大事件：主角不在追蹤池的重大新聞（IPO / 私人公司…）。僅資訊呈現，不進決策。 */
interface OutOfUniverseEvent {
  name: string;
  sentiment: number;
  impact: number;
  reason: string;
  headline: string;
  link: string;
  pubDate?: string;
  asOf: string;
}

const CATEGORY_LABEL: Record<CatalystEvent['category'], string> = {
  supplier_retreat: '🏭 大廠退出',
  policy: '🏛 政策／補貼',
  demand_shift: '🔄 需求轉變',
  capex: '💰 資本支出',
  supply_chain: '🔗 供應鏈',
  other: '📌 其他',
};

const SETUP_LABEL: Record<CatalystPick['setupType'], string> = {
  second_order_beneficiary: '二階受惠',
  policy_play: '政策受惠',
  supply_chain: '供應鏈',
  direct: '直接相關',
};

const HORIZON_LABEL: Record<CatalystPick['horizon'], string> = {
  short: '短線(1~5日)',
  mid: '中線(1~4週)',
  long: '長線(>1月)',
};

/** 把 ISO 時間格式化成「6/07 14:30」這種精簡新聞時間（給時間範圍用）。 */
function fmtNewsTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

interface Props {
  onSelectSymbol?: (s: string) => void;
  /** 被 PanelCard 等外層卡包裹時：去掉自己的「事件驅動雷達」標題 / 外框 / 關閉鈕（外層已有標題）。 */
  embedded?: boolean;
}

export function CatalystPanel({ onSelectSymbol, embedded = false }: Props) {
  const [market, setMarket] = useState<'tw' | 'us' | 'mixed'>('mixed');
  const [model, setModel] = useState<'flash' | 'pro'>('pro');
  const [visible, setVisible] = useState(true);
  // 常態顯示市場評論 + 受惠/受壓 symbol chips（精華）；
  // 細節（events 列表 + 完整 PickItem）用 FillExpand 填補等高留白，超出才「展開全部」。
  const qc = useQueryClient();

  // 自動讀盤前 timer 寫入的 catalyst cache；只讀 cache（nocache=0），
  // 後端 cold cache 時 retry 1 次避免 PRO LLM 即興算（30 秒 SWA gateway 會 timeout）
  const q = useQuery<CatalystResp>({
    queryKey: ['catalysts', market, model],
    queryFn: () => fetchJSON<CatalystResp>(`/api/catalysts?market=${market}&model=${model}`),
    retry: false,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // 池外重大事件（独立來源）：topic-watch timer 寫的 cache，只讀不打 LLM。不隨 market/model 變。
  const oouQ = useQuery<{ events: OutOfUniverseEvent[]; count: number; asOf?: string }>({
    queryKey: ['market-events-oou'],
    queryFn: () => fetchJSON('/api/market-events/out-of-universe'),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const outOfUniverse = oouQ.data?.events ?? [];

  // 手動重新整理：預設只重抓 cache（timer 每天盤前已預算），不 bypass cache。
  // 真的要強制重跑 PRO LLM（30 秒+），請打開「強制重算」勾選。
  const [forceRecompute, setForceRecompute] = useState(false);
  const m = useMutation({
    mutationFn: async (params: { mk: 'tw' | 'us' | 'mixed'; md: 'flash' | 'pro'; force: boolean }): Promise<CatalystResp> => {
      const qs = `market=${params.mk}&model=${params.md}${params.force ? '&nocache=1' : ''}`;
      return fetchJSON<CatalystResp>(`/api/catalysts?${qs}`);
    },
    onSuccess: (data, vars) => {
      qc.setQueryData(['catalysts', vars.mk, vars.md], data);
    },
  });

  // symbol 切換時自動展開（保持向後相容）
  useEffect(() => {
    setVisible(true);
  }, []);

  function run() {
    setVisible(true);
    m.mutate({ mk: market, md: model, force: forceRecompute });
  }

  const data = m.data ?? q.data;
  const isPending = m.isPending || (q.isFetching && !data);
  const error = m.error ?? q.error;

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          aria-label="選擇市場"
          value={market}
          onChange={(e) => setMarket(e.target.value as 'tw' | 'us' | 'mixed')}
          className="h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-xs text-(--text)"
        >
          <option value="tw">台股</option>
          <option value="us">美股</option>
          <option value="mixed">美 + 台</option>
        </select>
        <select
          aria-label="模型"
          value={model}
          onChange={(e) => setModel(e.target.value as 'flash' | 'pro')}
          className="h-8 rounded border border-(--border) bg-(--bg-card) px-2 text-xs text-(--text)"
          title="Pro 推理較強但較吃 quota（免費 tier 50 req/day）"
        >
          <option value="flash">Flash（省 quota）</option>
          <option value="pro">Pro（深推論）</option>
        </select>
        <button
          type="button"
          onClick={run}
          disabled={m.isPending}
          className="h-8 inline-flex items-center gap-1 rounded border border-(--border) px-2 text-xs text-(--text-secondary) hover:border-(--accent) disabled:opacity-50"
          title="預設只重抓盤前排程寫的快取（秒回）；勾「強制重算」才會跑 30 秒高品質模型"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${m.isPending ? 'animate-spin' : ''}`} />
          {m.isPending ? '重新抓中…' : '重新抓'}
        </button>
        <label className="text-[10px] text-(--text-muted) inline-flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={forceRecompute} onChange={(e) => setForceRecompute(e.target.checked)} className="h-3 w-3" />
          強制重算（30秒+，會吃 quota）
        </label>
      </div>

      {visible && (
        <div className={embedded ? 'mt-2 flex-1 flex flex-col min-h-0' : 'mt-3 rounded border border-(--border) bg-(--bg-card) p-3'}>
          {embedded ? (
            data && (
              <div className="mb-2 text-[10px] text-(--text-muted)">
                {data.newsCount} 則新聞 · {data.modelUsed} · {new Date(data.computedAt).toLocaleTimeString()}
                {data.fromCache ? ' · cache' : ''} · 每日 TW 06:30 / US 09:00 預排
              </div>
            )
          ) : (
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-(--accent)">
                <Radar className="inline h-4 w-4 mr-1" />事件驅動雷達
                {data && (
                  <span className="ml-2 text-[10px] font-normal text-(--text-muted)">
                    ({data.newsCount} 則新聞 · {data.modelUsed} · {new Date(data.computedAt).toLocaleTimeString()}
                    {data.fromCache ? ' · cache' : ''})
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => setVisible(false)}
                className="text-(--text-muted) hover:text-(--text)"
                aria-label="關閉"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* 🌐 池外重大事件：主角不在追蹤池（IPO / 私人公司…）— 僅資訊呈現，不進決策 */}
          {outOfUniverse.length > 0 && (
            <div className="mb-2 rounded border border-(--accent)/30 bg-(--accent)/5 p-2">
              <div className="text-[11px] font-semibold text-(--accent) mb-1 flex items-center gap-1 flex-wrap">
                🌐 池外重大事件
                <span className="text-[9px] font-normal text-(--text-muted)">不在追蹤池 · 僅供參考 · 不進決策</span>
              </div>
              <ul className="space-y-1">
                {outOfUniverse.map((e, i) => {
                  const tone = e.sentiment >= 0.3 ? 'text-(--up)' : e.sentiment <= -0.3 ? 'text-(--down)' : 'text-(--text-muted)';
                  const mark = e.sentiment >= 0.3 ? '▲' : e.sentiment <= -0.3 ? '▼' : '•';
                  return (
                    <li key={i} className="text-[11px] flex items-start gap-1.5 min-w-0">
                      <span className={`shrink-0 ${tone}`} title={`sentiment ${e.sentiment.toFixed(1)} · impact ${e.impact.toFixed(1)}`}>{mark}</span>
                      <span className="shrink-0 font-bold text-(--text-primary)">{e.name}</span>
                      <a
                        href={e.link}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="truncate text-(--text-secondary) hover:text-(--accent) inline-flex items-center gap-0.5 min-w-0"
                        title={e.headline}
                      >
                        <span className="truncate">{e.reason || e.headline}</span>
                        <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {isPending && (
            <div className="text-(--text-muted) text-sm py-6 text-center">
              讀取事件雷達快取…
            </div>
          )}
          {error && !isPending && !data && (
            <CatalystError error={error as Error} onRetry={() => run()} />
          )}

          {data && (
            <div className="space-y-3 flex-1 flex flex-col min-h-0">
              {data.marketComment && (
                <div className="p-2 rounded bg-(--bg-base) border border-(--border) text-[12px] text-(--text-secondary) leading-relaxed">
                  📊 {data.marketComment}
                </div>
              )}

              {/* === 摘要列（常態可見）：受惠 / 受壓 symbol chips + 事件數 === */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                  <span className="text-(--text-muted)">🎯 {data.events.length} 事件</span>
                  {data.beneficiaries.length > 0 && (
                    <>
                      <TrendingUp className="h-3 w-3 text-(--up)" />
                      {data.beneficiaries.slice(0, 6).map((p, i) => (
                        <button
                          key={`b${i}`}
                          type="button"
                          onClick={() => onSelectSymbol?.(p.symbol)}
                          className="px-1.5 py-0.5 rounded border border-(--up)/40 bg-(--up)/10 text-(--up) font-bold hover:bg-(--up)/20"
                          title={p.thesis}
                        >
                          {p.symbol}
                        </button>
                      ))}
                    </>
                  )}
                  {data.losers.length > 0 && (
                    <>
                      <TrendingDown className="h-3 w-3 text-(--down) ml-1" />
                      {data.losers.slice(0, 4).map((p, i) => (
                        <button
                          key={`l${i}`}
                          type="button"
                          onClick={() => onSelectSymbol?.(p.symbol)}
                          className="px-1.5 py-0.5 rounded border border-(--down)/40 bg-(--down)/10 text-(--down) font-bold hover:bg-(--down)/20"
                          title={p.thesis}
                        >
                          {p.symbol}
                        </button>
                      ))}
                    </>
                  )}
                </div>
                {/* 事件標題清單：收合時當「摘要」（每條 catalyst 一行：#id + 類別 badge + 標題），#id 對應展開後的事件詳情 */}
                {data.events.length > 0 && (
                  <ul className="space-y-1 pt-0.5">
                    {data.events.map((e) => (
                      <li key={e.id} className="flex items-center gap-1.5 text-[12px] min-w-0">
                        <span className="shrink-0 text-[10px] text-(--text-muted)">#{e.id}</span>
                        <span className="shrink-0 text-[10px] px-1 py-0.5 rounded bg-(--accent)/12 text-(--accent)">{CATEGORY_LABEL[e.category]}</span>
                        <span className="truncate text-(--text-secondary)" title={e.title}>{e.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* === 細節：FillExpand 填補等高留白，超出才「展開全部」 === */}
              <FillExpand
                moreLabel={`展開全部（${data.events.length} 事件 · ${data.beneficiaries.length + data.losers.length} 標的）`}
                lessLabel="收合細節"
              >
                <div className="space-y-3">
              {/* 新聞時間範圍：展開時顯示來源新聞的發布時間跨度（舊快取可能無 newsTimeRange） */}
              {data.newsTimeRange && (
                <div className="text-[11px] text-(--text-muted) flex items-center gap-1.5 flex-wrap">
                  <span>📅 新聞時間範圍：{fmtNewsTime(data.newsTimeRange.from)} ～ {fmtNewsTime(data.newsTimeRange.to)}</span>
                  <span className="text-(--text-muted)">· 共 {data.newsCount} 則</span>
                </div>
              )}
              {/* === Events === */}
              {data.events.length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold text-(--text-secondary) mb-1.5">
                    📋 事件詳情（摘要 ＋ 來源）
                  </h4>
                  <ul className="space-y-2">
                    {data.events.map((e) => (
                      <li key={e.id} className="p-2 rounded bg-(--bg-base) border border-(--border)">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded border border-(--border) text-(--text-muted)">
                            #{e.id}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-(--accent)/15 text-(--accent)">
                            {CATEGORY_LABEL[e.category]}
                          </span>
                          <span className="text-[10px] text-(--text-muted)">{e.geography}</span>
                        </div>
                        {/* 標題已在上方收合摘要列出；此處只補「展開才看的」摘要內文 + 來源，避免重複標題 */}
                        <div className="text-[12px] text-(--text-secondary) leading-relaxed">{e.summary}</div>
                        {e.sources.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {e.sources.map((s, i) => (
                              <a
                                key={i}
                                href={s.link}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-(--border) hover:bg-(--bg-card) text-(--text-muted) hover:text-(--accent) max-w-full truncate"
                                title={s.title}
                              >
                                <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                                <span className="truncate">{s.source || s.title.slice(0, 28)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* === Beneficiaries === */}
              {data.beneficiaries.length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold text-(--up) mb-1.5 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> 相對受惠（{data.beneficiaries.length}）
                  </h4>
                  <ul className="space-y-2">
                    {data.beneficiaries.map((p, i) => (
                      <PickItem key={i} pick={p} kind="up" onSelectSymbol={onSelectSymbol} />
                    ))}
                  </ul>
                </section>
              )}

              {/* === Losers === */}
              {data.losers.length > 0 && (
                <section>
                  <h4 className="text-xs font-semibold text-(--down) mb-1.5 flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" /> 相對受壓（{data.losers.length}）
                  </h4>
                  <ul className="space-y-2">
                    {data.losers.map((p, i) => (
                      <PickItem key={i} pick={p} kind="down" onSelectSymbol={onSelectSymbol} />
                    ))}
                  </ul>
                </section>
              )}
              {/* 免責聲明：放進 FillExpand 內最後一段，跟著細節一起被填補 / 展開 */}
              <div className="text-[10px] text-(--text-muted) leading-relaxed pt-2 border-t border-(--border)">
                ⚠ 本內容為 AI 對公開新聞的整理與關聯推論，<b className="text-(--text-secondary)">AI 輔助、扣板機自負</b>。
                受惠／受害關係不必然發生，請自行查證新聞、財報、訂單實況。
              </div>
                </div>
              </FillExpand>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function PickItem({
  pick,
  kind,
  onSelectSymbol,
}: {
  pick: CatalystPick;
  kind: 'up' | 'down';
  onSelectSymbol?: (s: string) => void;
}) {
  const colorVar = kind === 'up' ? '--up' : '--down';
  const confidencePct = Math.round(pick.confidence * 100);

  return (
    <li className={`p-2 rounded bg-(--bg-base) border border-[var(${colorVar})]/30`}>
      <div className="flex items-start gap-2 flex-wrap mb-1">
        <button
          type="button"
          onClick={() => onSelectSymbol?.(pick.symbol)}
          className={`font-bold text-sm text-[var(${colorVar})] hover:underline`}
          title="加入自選 / 切換"
        >
          {pick.symbol}
        </button>
        {pick.name && <span className="text-[11px] text-(--text-muted)">{pick.name}</span>}
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-(--border) text-(--text-secondary)">
          {SETUP_LABEL[pick.setupType]}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-(--border) text-(--text-secondary)">
          {HORIZON_LABEL[pick.horizon]}
        </span>
        <span className={`ml-auto text-[10px] font-bold tnum text-[var(${colorVar})]`}>
          {confidencePct}%
        </span>
      </div>
      <div className="text-[12px] text-(--text-primary) leading-relaxed">{pick.thesis}</div>
      {pick.reasoning && (
        <div className="text-[11px] text-(--text-secondary) leading-relaxed mt-0.5">{pick.reasoning}</div>
      )}
      <div className="flex items-center gap-1 mt-1 flex-wrap">
        <span className="text-[10px] text-(--text-muted)">關聯事件：</span>
        {pick.relatedEventIds.map((id) => (
          <span key={id} className="text-[10px] px-1 rounded bg-(--accent)/10 text-(--accent)">
            #{id}
          </span>
        ))}
      </div>
      {(pick.watchSignals.length > 0 || pick.risks.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-1.5 text-[10px]">
          {pick.watchSignals.length > 0 && (
            <div>
              <div className="text-(--text-muted) mb-0.5">🔭 觀察</div>
              <ul className="space-y-0.5">
                {pick.watchSignals.map((s, i) => (
                  <li key={i} className="text-(--text-secondary) leading-relaxed">▸ {s}</li>
                ))}
              </ul>
            </div>
          )}
          {pick.risks.length > 0 && (
            <div>
              <div className="text-(--warn) mb-0.5 flex items-center gap-0.5">
                <AlertTriangle className="h-2.5 w-2.5" /> 風險
              </div>
              <ul className="space-y-0.5">
                {pick.risks.map((r, i) => (
                  <li key={i} className="text-(--text-secondary) leading-relaxed">▸ {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function CatalystError({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const msg = error.message || '';
  const lower = msg.toLowerCase();
  const isAiDown =
    msg.includes('503') ||
    msg.includes('429') ||
    lower.includes('quota') ||
    lower.includes('resource_exhausted') ||
    lower.includes('ai_unavailable');
  if (isAiDown) {
    return (
      <div className="p-3 rounded border border-(--warn)/40 bg-(--warn)/10">
        <div className="flex items-center gap-1.5 text-(--warn) font-bold text-sm mb-1">
          ⚠️ AI 服務暫時忙碌
        </div>
        <div className="text-[11px] text-(--text-secondary)">
          事件雷達需要 LLM 推論，主備服務都未回應。請稍後再試。
        </div>
      </div>
    );
  }
  return (
    <div className="p-3 rounded border border-(--up)/40 bg-(--up)/10 text-(--up) text-sm flex items-center justify-between gap-2 flex-wrap">
      <span>事件雷達失敗：{error.message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="px-2 py-0.5 rounded border border-(--up)/60 text-[11px] hover:bg-(--up)/20">重試</button>
      )}
    </div>
  );
}

